import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import {
  HomeIcon,
  ChatBubbleLeftRightIcon,
  ShieldCheckIcon,
  EyeIcon,
  ScaleIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  UserGroupIcon,
  DocumentCheckIcon,
  CurrencyDollarIcon,
  ChartPieIcon,
  Cog6ToothIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentCheckIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import LanguageSwitcher from '../LanguageSwitcher';
import { normalizeUserRole } from '../../hooks/usePermissions';
import { useUnreadMessageCount } from '../../hooks/useUnreadMessageCount';

interface NavigationProps {
  onLogout: () => void;
}

interface NavItem {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: string[];
}

export default function Navigation({ onLogout }: NavigationProps) {
  const location = useLocation();
  const unreadMessageCount = useUnreadMessageCount();
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isInvestigationOpen, setIsInvestigationOpen] = useState(false);

  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const displayRole: string = user?.role || 'agent';
  const userRole = normalizeUserRole(displayRole);

  const hasAccess = (roles?: string[]): boolean => {
    if (!roles || roles.length === 0) return true;
    return roles.includes(userRole);
  };

  const mainNavItems: NavItem[] = [
    { name: 'Accueil', path: '/', icon: HomeIcon },
    { name: 'Messages', path: '/messages', icon: ChatBubbleLeftRightIcon },
    { name: 'Alertes', path: '/alerts', icon: ShieldCheckIcon },
    { name: 'Surveillance', path: '/watchlist', icon: EyeIcon },
    { name: 'Règles', path: '/scoring-rules', icon: ScaleIcon },
    { name: 'Scénarios', path: '/scenarios', icon: ExclamationTriangleIcon },
    { name: 'Statistiques', path: '/statistics', icon: ChartBarIcon },
    { name: 'Invités', path: '/guests', icon: UserGroupIcon },
    { name: 'Documents', path: '/documents', icon: DocumentCheckIcon },
    { name: 'Incidents', path: '/incidents', icon: ExclamationTriangleIcon },
    { name: 'Réglementations', path: '/regulations', icon: DocumentCheckIcon },
    { name: 'Relations', path: '/relationships', icon: UserGroupIcon },
  ];

  const investigationNavItems: NavItem[] = [
    { name: 'Recherche', path: '/investigation/search', icon: MagnifyingGlassIcon },
    { name: 'Géolocalisation', path: '/investigation/geo', icon: MapPinIcon },
    { name: 'Alertes Matching', path: '/investigation/alerts', icon: BellAlertIcon },
  ];

  const adminNavItems: NavItem[] = [
    { name: 'Dashboard Global', path: '/admin/dashboard', icon: ChartBarIcon, roles: ['admin', 'supervisor', 'analyst'] },
    { name: 'Établissements', path: '/admin/hotels', icon: BuildingOfficeIcon, roles: ['admin', 'supervisor'] },
    { name: 'Onboarding / KYC', path: '/admin/hotels/pending', icon: ClipboardDocumentCheckIcon, roles: ['admin'] },
    { name: 'Utilisateurs', path: '/admin/users', icon: UsersIcon, roles: ['admin'] },
    { name: 'Configuration Taxes', path: '/tax-config', icon: CurrencyDollarIcon, roles: ['admin', 'supervisor'] },
    { name: 'Paramètres Système', path: '/admin/settings', icon: Cog6ToothIcon, roles: ['admin'] },
    { name: 'Dashboard Opérationnel', path: '/operational', icon: ChartPieIcon, roles: ['admin', 'supervisor'] },
    { name: "Logs d'Audit", path: '/audit-logs', icon: ClipboardDocumentCheckIcon, roles: ['admin'] },
    { name: 'Monitoring Santé', path: '/health-monitoring', icon: ChartBarIcon, roles: ['admin', 'supervisor'] },
  ];

  const visibleAdminItems = adminNavItems.filter((item) => hasAccess(item.roles));
  const showAdminSection = visibleAdminItems.length > 0;
  const isInvestigationActive = location.pathname.startsWith('/investigation');

  const isActive = (path: string): boolean => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navLinkClass = (path: string): string => {
    const base = 'flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors';
    return isActive(path)
      ? `${base} bg-blue-800 text-white`
      : `${base} text-blue-100 hover:bg-blue-700 hover:text-white`;
  };

  return (
    <nav className="bg-blue-900 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center">
              <ShieldCheckIcon className="h-8 w-8 text-white mr-2" />
              <span className="text-xl font-bold text-white">CNS Dashboard</span>
            </Link>

            {/* Desktop */}
            <div className="hidden lg:flex items-center space-x-1 ml-6">
              {mainNavItems.slice(1, 5).map((item) => (
                <Link key={item.path} to={item.path} className={navLinkClass(item.path)}>
                  <item.icon className="h-5 w-5 mr-1.5" />
                  {item.name}
                  {item.path === '/messages' && unreadMessageCount > 0 && (
                    <span className="ml-1 rounded-full bg-red-500 px-1.5 text-[10px] leading-4 text-white">
                      {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                    </span>
                  )}
                </Link>
              ))}

              {/* Dropdown Investigation */}
              <div className="relative">
                <button
                  onClick={() => setIsInvestigationOpen(!isInvestigationOpen)}
                  className={[
                    'flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    isInvestigationActive
                      ? 'bg-blue-800 text-white'
                      : 'text-blue-100 hover:bg-blue-700 hover:text-white',
                  ].join(' ')}
                >
                  <MagnifyingGlassIcon className="h-5 w-5 mr-1.5" />
                  Investigation
                  {isInvestigationOpen
                    ? <ChevronUpIcon className="h-4 w-4 ml-1" />
                    : <ChevronDownIcon className="h-4 w-4 ml-1" />}
                </button>
                {isInvestigationOpen && (
                  <div className="absolute left-0 mt-2 w-52 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      {investigationNavItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsInvestigationOpen(false)}
                        >
                          <item.icon className="h-5 w-5 mr-2 text-gray-400" />
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Dropdown Administration */}
              {showAdminSection && (
                <div className="relative">
                  <button
                    onClick={() => setIsAdminOpen(!isAdminOpen)}
                    className={`${navLinkClass('/admin')} flex items-center`}
                  >
                    <Cog6ToothIcon className="h-5 w-5 mr-1.5" />
                    Administration
                    {isAdminOpen
                      ? <ChevronUpIcon className="h-4 w-4 ml-1" />
                      : <ChevronDownIcon className="h-4 w-4 ml-1" />}
                  </button>
                  {isAdminOpen && (
                    <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1">
                        {visibleAdminItems.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsAdminOpen(false)}
                          >
                            <item.icon className="h-5 w-5 mr-2 text-gray-400" />
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* User + logout */}
          <div className="flex items-center space-x-4">
            {user && (
              <div className="hidden md:flex items-center text-white text-sm">
                <div className="mr-3">
                  <div className="font-medium">{user.name || user.email}</div>
                  <div className="text-blue-200 text-xs">{displayRole}</div>
                </div>
              </div>
            )}
            <LanguageSwitcher />
            <button
              onClick={onLogout}
              className="px-4 py-2 text-sm text-white hover:text-blue-200 transition font-medium"
            >
              Déconnexion
            </button>
          </div>
        </div>

        {/* Mobile */}
        <div className="lg:hidden pb-3 space-y-1">
          {mainNavItems.map((item) => (
            <Link key={item.path} to={item.path} className={navLinkClass(item.path)}>
                <item.icon className="h-5 w-5 mr-2" />
                {item.name}
                {item.path === '/messages' && unreadMessageCount > 0 && (
                  <span className="ml-1 rounded-full bg-red-500 px-1.5 text-[10px] leading-4 text-white">
                    {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                  </span>
                )}
            </Link>
          ))}

          <div className="pt-2 pb-1">
            <div className="text-blue-300 text-xs font-semibold uppercase tracking-wider px-3">
              Investigation
            </div>
          </div>
          {investigationNavItems.map((item) => (
            <Link key={item.path} to={item.path} className={navLinkClass(item.path)}>
              <item.icon className="h-5 w-5 mr-2" />
              {item.name}
            </Link>
          ))}

          {showAdminSection && (
            <>
              <div className="pt-2 pb-1">
                <div className="text-blue-300 text-xs font-semibold uppercase tracking-wider px-3">
                  Administration
                </div>
              </div>
              {visibleAdminItems.map((item) => (
                <Link key={item.path} to={item.path} className={navLinkClass(item.path)}>
                  <item.icon className="h-5 w-5 mr-2" />
                  {item.name}
                </Link>
              ))}
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
